import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Shield, LogOut, RefreshCw, Search, TrendingUp, TrendingDown, Users, DollarSign, Package, Truck, Banknote, Check, X, Clock, MessageSquare, Bot, Radio, Swords } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TKEY = "uc_admin_token";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminPage() {
  const [token, setToken] = useState(() => localStorage.getItem(TKEY) || "");
  return token ? <Dashboard token={token} onLogout={() => { localStorage.removeItem(TKEY); setToken(""); }} />
                : <Login onLogin={(t) => { localStorage.setItem(TKEY, t); setToken(t); }} />;
}

function Login({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { username, password });
      onLogin(data.token);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Login failed");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0F0C16] text-[#E7E9F0] flex items-center justify-center px-4" style={{ fontFamily: "Inter, system-ui, sans-serif" }}>
      <form onSubmit={submit} data-testid="admin-login-form" className="w-full max-w-sm border border-[#2A2440] bg-[#19142A] p-8">
        <div className="flex items-center gap-2 mb-1">
          <Shield className="h-5 w-5 text-[#8B5CF6]" />
          <h1 className="text-lg font-extrabold tracking-tight">unboxcases admin</h1>
        </div>
        <p className="text-xs text-[#7C859B] mb-6">Restricted — authorized personnel only.</p>
        <label className="text-[11px] font-semibold uppercase tracking-wide text-[#7C859B]">Username</label>
        <input data-testid="admin-username" value={username} onChange={(e) => setUsername(e.target.value)} autoComplete="username"
          className="w-full mt-1 mb-4 bg-[#0F0C16] border border-[#2A2440] px-3 py-2.5 text-sm outline-none focus:border-[#8B5CF6]" />
        <label className="text-[11px] font-semibold uppercase tracking-wide text-[#7C859B]">Password</label>
        <input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
          className="w-full mt-1 mb-4 bg-[#0F0C16] border border-[#2A2440] px-3 py-2.5 text-sm outline-none focus:border-[#8B5CF6]" />
        {err && <p data-testid="admin-login-error" className="text-xs text-[#F43F5E] mb-3">{err}</p>}
        <button data-testid="admin-login-submit" disabled={loading}
          className="w-full py-2.5 bg-gradient-to-r from-[#8B5CF6] to-[#8B5CF6] text-white font-bold text-sm disabled:opacity-60">
          {loading ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}

function Stat({ icon: Icon, label, value, tint }) {
  return (
    <div className="border border-[#2A2440] bg-[#19142A] p-4">
      <div className="flex items-center gap-2 text-[#7C859B] text-[11px] font-semibold uppercase tracking-wide">
        <Icon className="h-3.5 w-3.5" style={{ color: tint }} /> {label}
      </div>
      <div className="mt-1 text-2xl font-extrabold font-mono">{value}</div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [sortKey, setSortKey] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [tab, setTab] = useState("users");
  const [invUser, setInvUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) {
      if (e?.response?.status === 401) { setErr("Session expired — please log in again."); }
      else setErr(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  let rows = data?.users || [];
  if (q) {
    const t = q.toLowerCase();
    rows = rows.filter((r) => (r.email || "").toLowerCase().includes(t) || (r.name || "").toLowerCase().includes(t));
  }
  rows = [...rows].sort((a, b) => {
    const va = a[sortKey], vb = b[sortKey];
    const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va || "").localeCompare(String(vb || ""));
    return sortDir === "asc" ? cmp : -cmp;
  });

  const setSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "asc" ? "desc" : "asc")); else { setSortKey(k); setSortDir("desc"); } };

  const cols = [
    ["email", "Account"], ["balance", "Balance"], ["total_deposited", "Deposited"],
    ["bonus_received", "Bonus"], ["cases_opened", "Cases"], ["battles_played", "Battles"],
    ["highest_win", "Highest Win"], ["pnl", "PnL"], ["created_at", "Joined"],
  ];

  return (
    <div className="min-h-screen bg-[#0F0C16] text-[#E7E9F0]" style={{ fontFamily: "Inter, system-ui, sans-serif" }} data-testid="admin-dashboard">
      <header className="border-b border-[#2A2440] bg-[#0F0C16] sticky top-0 z-10">
        <div className="mx-auto max-w-[1400px] px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-[#8B5CF6]" />
            <span className="font-extrabold tracking-tight">unboxcases admin</span>
          </div>
          <div className="flex items-center gap-2">
            <button data-testid="admin-refresh" onClick={load} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-[#2A2440] hover:bg-[#19142A]">
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button data-testid="admin-logout" onClick={onLogout} className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 border border-[#2A2440] hover:bg-[#19142A] text-[#F43F5E]">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-6">
        {err && <div data-testid="admin-error" className="mb-4 border border-[#F43F5E]/40 bg-[#F43F5E]/10 px-4 py-3 text-sm text-[#F43F5E]">{err}</div>}

        <div className="flex gap-2 mb-6">
          <button data-testid="admin-tab-users" onClick={() => setTab("users")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "users" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <Users className="h-3.5 w-3.5" /> Users
          </button>
          <button data-testid="admin-tab-withdrawals" onClick={() => setTab("withdrawals")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "withdrawals" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <Banknote className="h-3.5 w-3.5" /> Withdrawals
          </button>
          <button data-testid="admin-tab-tickets" onClick={() => setTab("tickets")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "tickets" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <MessageSquare className="h-3.5 w-3.5" /> Support
          </button>
          <button data-testid="admin-tab-balance" onClick={() => setTab("balance")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "balance" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <DollarSign className="h-3.5 w-3.5" /> Balance
          </button>
          <button data-testid="admin-tab-bots" onClick={() => setTab("bots")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "bots" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <Bot className="h-3.5 w-3.5" /> Bots
          </button>
          <button data-testid="admin-tab-affiliates" onClick={() => setTab("affiliates")}
            className={`flex items-center gap-1.5 text-xs font-bold uppercase px-4 py-2 border ${tab === "affiliates" ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>
            <Users className="h-3.5 w-3.5" /> Affiliates
          </button>
        </div>

        {tab === "withdrawals" && <Withdrawals token={token} />}
        {tab === "tickets" && <Tickets token={token} />}
        {tab === "balance" && <Balance token={token} />}
        {tab === "bots" && <Bots token={token} />}
        {tab === "affiliates" && <Affiliates token={token} />}
        {tab === "users" && (<>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat icon={Users} label="Users" value={s.total_users ?? "—"} tint="#8B5CF6" />
          <Stat icon={DollarSign} label="Total Deposited" value={money(s.total_deposited)} tint="#34D399" />
          <Stat icon={DollarSign} label="Bonus Given" value={money(s.total_bonus)} tint="#FBBF24" />
          <Stat icon={Package} label="Cases Opened" value={s.total_cases_opened ?? "—"} tint="#A78BFA" />
          <Stat icon={s.player_pnl >= 0 ? TrendingUp : TrendingDown} label="Player PnL" value={money(s.player_pnl)} tint={s.player_pnl >= 0 ? "#34D399" : "#F43F5E"} />
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex items-center gap-2 border border-[#2A2440] bg-[#19142A] px-3 py-2 w-full max-w-xs">
            <Search className="h-4 w-4 text-[#7C859B]" />
            <input data-testid="admin-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or name"
              className="bg-transparent text-sm outline-none w-full" />
          </div>
          <span className="text-xs text-[#7C859B]">{rows.length} shown</span>
        </div>
        <p className="text-[11px] text-[#586074] mb-2">Tip: click any user row to view their inventory.</p>

        <div className="border border-[#2A2440] overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[#0F0C16] text-[#7C859B]">
              <tr>
                {cols.map(([k, label]) => (
                  <th key={k} onClick={() => setSort(k)}
                    className={`px-3 py-3 text-left font-semibold uppercase text-[11px] tracking-wide cursor-pointer whitespace-nowrap select-none ${k !== "email" ? "text-right" : ""}`}>
                    {label}{sortKey === k ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && !data ? (
                <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-[#7C859B]">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={cols.length} className="px-3 py-10 text-center text-[#7C859B]">No users found</td></tr>
              ) : rows.map((r) => (
                <tr key={r.id} data-testid={`admin-user-row`} onClick={() => setInvUser(r)}
                  title="Click to view inventory"
                  className="border-t border-[#1E1832] hover:bg-[#120F22] cursor-pointer">
                  <td className="px-3 py-2.5">
                    <div className="font-semibold text-[#E7E9F0] flex items-center gap-1.5"><Package className="h-3 w-3 text-[#8B5CF6]" />{r.name}</div>
                    <div className="text-[11px] text-[#7C859B]">{r.email}</div>
                  </td>
                  <td className="px-3 py-2.5 text-right font-mono">{money(r.balance)}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{money(r.total_deposited)}</td>
                  <td className="px-3 py-2.5 text-right font-mono text-[#FBBF24]">{r.bonus_received > 0 ? money(r.bonus_received) : "—"}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.cases_opened}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.battles_played}</td>
                  <td className="px-3 py-2.5 text-right font-mono">{r.highest_win > 0 ? money(r.highest_win) : "—"}</td>
                  <td className={`px-3 py-2.5 text-right font-mono font-bold ${r.pnl > 0 ? "text-[#34D399]" : r.pnl < 0 ? "text-[#F43F5E]" : "text-[#7C859B]"}`}>
                    {r.pnl > 0 ? "+" : ""}{money(r.pnl)}
                  </td>
                  <td className="px-3 py-2.5 text-right text-[11px] text-[#7C859B] whitespace-nowrap">
                    {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-[11px] text-[#586074] mt-3">PnL = total value won − total wagered (case prices + battle entries). Deposited = real Stripe payments (excludes bonus).</p>
        </>)}
      </main>
      {invUser && <UserInventoryModal token={token} user={invUser} onClose={() => setInvUser(null)} />}
    </div>
  );
}

function Withdrawals({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState("");
  const [filter, setFilter] = useState("pending");

  const load = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/withdrawals`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to load"); }
    setLoading(false);
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const act = async (wid, action) => {
    setBusy(wid + action);
    try {
      await axios.post(`${API}/admin/withdrawals/${wid}/action`, { action }, { headers: { Authorization: `Bearer ${token}` } });
      await load();
    } catch (e) { setErr(e?.response?.data?.detail || "Action failed"); }
    setBusy("");
  };

  const s = data?.summary || {};
  let list = data?.withdrawals || [];
  if (filter !== "all") list = list.filter((w) => w.status === filter);

  const badge = (status) => {
    const map = { pending: "#FBBF24", approved: "#8B5CF6", fulfilled: "#34D399", rejected: "#F43F5E" };
    return <span className="px-2 py-0.5 text-[10px] font-bold uppercase" style={{ color: map[status], border: `1px solid ${map[status]}55` }}>{status}</span>;
  };

  return (
    <div data-testid="admin-withdrawals">
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={Clock} label="Pending" value={s.pending ?? "—"} tint="#FBBF24" />
        <Stat icon={Banknote} label="Cash Pending" value={money(s.cash_pending_value)} tint="#34D399" />
        <Stat icon={Truck} label="Ship Pending" value={s.ship_pending ?? "—"} tint="#A78BFA" />
      </div>
      <div className="flex flex-wrap gap-2 mb-3">
        {["pending", "approved", "fulfilled", "rejected", "all"].map((f) => (
          <button key={f} data-testid={`wd-filter-${f}`} onClick={() => setFilter(f)}
            className={`text-[11px] font-bold uppercase px-3 py-1.5 border ${filter === f ? "bg-[#19142A] border-[#8B5CF6] text-white" : "border-[#2A2440] text-[#7C859B] hover:bg-[#19142A]"}`}>{f}</button>
        ))}
        <button data-testid="wd-refresh" onClick={load} className="ml-auto text-[11px] font-bold px-3 py-1.5 border border-[#2A2440] hover:bg-[#19142A] flex items-center gap-1"><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      {err && <div data-testid="admin-wd-error" className="mb-3 border border-[#F43F5E]/40 bg-[#F43F5E]/10 px-4 py-2 text-sm text-[#F43F5E]">{err}</div>}
      <div className="border border-[#2A2440] overflow-x-auto">
        <table className="w-full text-sm" data-testid="admin-withdrawals-table">
          <thead className="bg-[#0F0C16] text-[#7C859B]">
            <tr>
              {["Type", "Account", "Details", "Value", "Status", "Requested", "Actions"].map((h) => (
                <th key={h} className="px-3 py-3 text-left font-semibold uppercase text-[11px] tracking-wide whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && !data ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#7C859B]">Loading…</td></tr>
            ) : list.length === 0 ? (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-[#7C859B]">No {filter !== "all" ? filter : ""} withdrawals</td></tr>
            ) : list.map((w) => (
              <tr key={w.id} data-testid="admin-withdrawal-row" className="border-t border-[#1E1832] hover:bg-[#120F22] align-top">
                <td className="px-3 py-2.5">
                  <span className="flex items-center gap-1.5 font-semibold">
                    {w.type === "cash" ? <Banknote className="h-3.5 w-3.5 text-[#34D399]" /> : <Truck className="h-3.5 w-3.5 text-[#A78BFA]" />}
                    {w.type === "cash" ? "Cash" : "Ship"}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="font-semibold">{w.user_name}</div>
                  <div className="text-[11px] text-[#7C859B]">{w.user_email}</div>
                </td>
                <td className="px-3 py-2.5 text-[11px] text-[#B7BECC] max-w-[280px]">
                  {w.type === "cash"
                    ? <div>
                        <div className="text-[#E7E9F0]">{(w.items || [{ item_name: w.item_name }]).map((x) => x.item_name).join(", ")}</div>
                        <span className="uppercase text-[#7C859B]">{w.method}</span>{w.destination ? ` · ${w.destination}` : ""}
                        {w.bank && <div className="mt-0.5 text-[#E7E9F0]">{w.bank.account_holder} · Acct {w.bank.account_number} · Routing {w.bank.routing_number}</div>}
                      </div>
                    : <div>
                        <div className="font-semibold text-[#E7E9F0]">{(w.items || [{ item_name: w.item_name }]).map((x) => x.item_name).join(", ")}</div>
                        <div>{w.ship?.full_name}, {w.ship?.address1}, {w.ship?.city}, {w.ship?.state} {w.ship?.zip}, {w.ship?.country}</div>
                        {w.ship?.note && <div className="italic">“{w.ship.note}”</div>}
                      </div>}
                </td>
                <td className="px-3 py-2.5 font-mono">{money(w.amount ?? w.item_value)}</td>
                <td className="px-3 py-2.5">{badge(w.status)}</td>
                <td className="px-3 py-2.5 text-[11px] text-[#7C859B] whitespace-nowrap">{w.created_at ? new Date(w.created_at).toLocaleString() : "—"}</td>
                <td className="px-3 py-2.5">
                  {["fulfilled", "rejected"].includes(w.status) ? (
                    <span className="text-[11px] text-[#586074]">Closed</span>
                  ) : (
                    <div className="flex gap-1.5">
                      {w.status === "pending" && (
                        <button data-testid="wd-approve-btn" disabled={!!busy} onClick={() => act(w.id, "approve")}
                          className="px-2 py-1 text-[10px] font-bold border border-[#8B5CF6]/50 text-[#8B5CF6] hover:bg-[#8B5CF6]/10 disabled:opacity-50">Approve</button>
                      )}
                      <button data-testid="wd-fulfill-btn" disabled={!!busy} onClick={() => act(w.id, "fulfill")}
                        className="px-2 py-1 text-[10px] font-bold border border-[#34D399]/50 text-[#34D399] hover:bg-[#34D399]/10 disabled:opacity-50 flex items-center gap-1"><Check className="h-3 w-3" />Fulfill</button>
                      <button data-testid="wd-reject-btn" disabled={!!busy} onClick={() => act(w.id, "reject")}
                        className="px-2 py-1 text-[10px] font-bold border border-[#F43F5E]/50 text-[#F43F5E] hover:bg-[#F43F5E]/10 disabled:opacity-50 flex items-center gap-1"><X className="h-3 w-3" />Reject</button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-[11px] text-[#586074] mt-3">Reject a cash request to auto-refund the user's balance. Fulfill a ship request to mark the item shipped. Payouts are made manually outside the platform.</p>
    </div>
  );
}



function Balance({ token }) {
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState("");
  const H = { Authorization: `Bearer ${token}` };

  const submit = async (sign) => {
    setErr(""); setMsg(null);
    const amt = Number(amount) * sign;
    if (!email.trim()) { setErr("Enter an account email."); return; }
    if (!amt) { setErr("Enter a non-zero amount."); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/users/credit`, { email: email.trim(), amount: amt }, { headers: H });
      setMsg(data);
      setAmount("");
    } catch (e) { setErr(e?.response?.data?.detail || "Failed to update balance."); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-balance" className="max-w-md">
      <div className="text-xs text-[#7C859B] mb-3 flex items-center gap-1.5"><DollarSign className="h-3.5 w-3.5" /> Add or remove site credits from any account by email.</div>
      <div className="border border-[#2A2440] bg-[#19142A] p-4 space-y-3">
        <div>
          <label className="text-[10px] font-bold uppercase text-[#7C859B]">Account email</label>
          <input data-testid="balance-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="player@example.com"
            className="w-full mt-1 bg-[#0F0C16] border border-[#2A2440] text-white text-sm px-3 py-2 outline-none focus:border-[#8B5CF6]" />
        </div>
        <div>
          <label className="text-[10px] font-bold uppercase text-[#7C859B]">Amount (USD)</label>
          <input data-testid="balance-amount" type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="50.00"
            className="w-full mt-1 bg-[#0F0C16] border border-[#2A2440] text-white text-sm px-3 py-2 outline-none focus:border-[#8B5CF6]" />
        </div>
        {err && <div className="text-xs text-[#F87171] bg-[#F87171]/10 border border-[#F87171]/30 px-3 py-2">{err}</div>}
        {msg && (
          <div data-testid="balance-result" className="text-xs text-[#8B5CF6] bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 px-3 py-2">
            {msg.amount >= 0 ? "Added" : "Removed"} {money(Math.abs(msg.amount))} {msg.amount >= 0 ? "to" : "from"} <b>{msg.name || msg.email}</b> — new balance <b>{money(msg.credits)}</b>
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <button data-testid="balance-add-btn" disabled={busy} onClick={() => submit(1)}
            className="flex-1 bg-[#16A34A] hover:bg-[#15903f] text-white text-xs font-bold uppercase py-2.5 disabled:opacity-40">
            + Add credit
          </button>
          <button data-testid="balance-remove-btn" disabled={busy} onClick={() => submit(-1)}
            className="flex-1 border border-[#2A2440] text-[#AEB6C6] hover:bg-[#19142A] text-xs font-bold uppercase py-2.5 disabled:opacity-40">
            − Remove
          </button>
        </div>
      </div>
    </div>
  );
}

function Tickets({ token }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const H = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/tickets`, { headers: H }); setData(data); }
    catch (_) { setData({ tickets: [], summary: { total: 0, open: 0 } }); }
  }, [token]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const action = async (id, act) => {
    setBusy(id);
    try { await axios.post(`${API}/admin/tickets/${id}/action`, { action: act }, { headers: H }); await load(); }
    catch (_) {} finally { setBusy(""); }
  };

  if (!data) return <div className="text-[#7C859B] text-sm py-10 text-center">Loading…</div>;
  return (
    <div data-testid="admin-tickets">
      <div className="flex gap-3 mb-4">
        <div className="flex-1 border border-[#2A2440] bg-[#19142A] p-3"><div className="text-[10px] uppercase text-[#7C859B]">Total tickets</div><div className="text-xl font-bold text-white">{data.summary.total}</div></div>
        <div className="flex-1 border border-[#2A2440] bg-[#19142A] p-3"><div className="text-[10px] uppercase text-[#7C859B]">Open</div><div className="text-xl font-bold text-[#8B5CF6]">{data.summary.open}</div></div>
      </div>
      {data.tickets.length === 0 ? (
        <div className="text-[#7C859B] text-sm py-10 text-center flex flex-col items-center gap-2"><MessageSquare className="h-6 w-6" /> No support tickets yet.</div>
      ) : (
        <div className="space-y-2">
          {data.tickets.map((t) => (
            <div key={t.id} data-testid="admin-ticket-row" className="border border-[#2A2440] bg-[#19142A] p-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-sm font-semibold text-white flex items-center gap-2">
                    {t.name || "Guest"} <span className="text-[#7C859B] font-normal text-xs">{t.email}</span>
                  </div>
                  <div className="text-xs text-[#AEB6C6] mt-1 whitespace-pre-wrap">{t.message}</div>
                  <div className="text-[10px] text-[#5E6779] mt-1">{t.created_at ? new Date(t.created_at).toLocaleString() : ""} · {t.transcript?.length || 0} chat msgs</div>
                </div>
                <div className="flex flex-col items-end gap-2 shrink-0">
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 border" style={{ color: t.status === "open" ? "#8B5CF6" : "#16A34A", borderColor: (t.status === "open" ? "#8B5CF6" : "#16A34A") + "55" }}>{t.status}</span>
                  <button disabled={busy === t.id} onClick={() => action(t.id, t.status === "open" ? "close" : "open")}
                    className="text-[10px] font-bold uppercase px-2 py-1 border border-[#2A2440] text-[#AEB6C6] hover:bg-[#19142A]">
                    {t.status === "open" ? "Close" : "Reopen"}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Bots({ token }) {
  const [data, setData] = useState(null);
  const H = { Authorization: `Bearer ${token}` };
  const load = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/admin/bots`, { headers: H }); setData(data); }
    catch (_) { setData({ battles: [], battles_summary: {}, drops: [] }); }
  }, [token]); // eslint-disable-line
  useEffect(() => { load(); const i = setInterval(load, 15000); return () => clearInterval(i); }, [load]);

  if (!data) return <div className="text-[#7C859B] text-sm py-10 text-center">Loading…</div>;
  const s = data.battles_summary || {};
  return (
    <div data-testid="admin-bots">
      <div className="text-xs text-[#7C859B] mb-3 flex items-center gap-1.5"><Bot className="h-3.5 w-3.5" /> Automated bot activity — kept separate from real players.</div>
      <div className="flex gap-3 mb-4">
        <div className="flex-1 border border-[#2A2440] bg-[#19142A] p-3"><div className="text-[10px] uppercase text-[#7C859B]">Bot battles</div><div className="text-xl font-bold text-white">{s.total || 0}</div></div>
        <div className="flex-1 border border-[#2A2440] bg-[#19142A] p-3"><div className="text-[10px] uppercase text-[#7C859B]">Waiting</div><div className="text-xl font-bold text-[#8B5CF6]">{s.waiting || 0}</div></div>
        <div className="flex-1 border border-[#2A2440] bg-[#19142A] p-3"><div className="text-[10px] uppercase text-[#7C859B]">Bot drops</div><div className="text-xl font-bold text-white">{data.drops.length}</div></div>
      </div>

      <div className="text-xs font-bold uppercase text-[#7C859B] mb-2 flex items-center gap-1.5"><Swords className="h-3.5 w-3.5" /> Bot-hosted battles</div>
      <div className="space-y-2 mb-6">
        {data.battles.length === 0 ? <div className="text-[#7C859B] text-sm py-4 text-center">No bot battles right now.</div> :
          data.battles.map((b) => (
            <div key={b.id} data-testid="admin-bot-battle-row" className="border border-[#2A2440] bg-[#19142A] p-2.5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-white truncate">{b.host} <span className="text-[10px] text-[#7C859B] font-normal">· {b.mode} · {b.cases} case{b.cases !== 1 ? "s" : ""}</span></div>
                <div className="text-[10px] text-[#7C859B] truncate">{(b.players || []).join(", ")}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {b.human_joined && <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 border border-[#F5B301]/50 text-[#F5B301]">human joined</span>}
                <span className="text-[10px] font-bold uppercase px-2 py-0.5 border border-[#2A2440] text-[#AEB6C6]">{b.status}</span>
              </div>
            </div>
          ))}
      </div>

      <div className="text-xs font-bold uppercase text-[#7C859B] mb-2 flex items-center gap-1.5"><Radio className="h-3.5 w-3.5" /> Bot live-drops</div>
      <div className="space-y-1.5">
        {data.drops.slice(0, 15).map((d, i) => (
          <div key={i} className="border border-[#2A2440] bg-[#19142A] px-2.5 py-1.5 flex items-center justify-between text-xs">
            <span className="text-[#AEB6C6] truncate"><span className="text-white font-semibold">{d.user}</span> · {d.name}</span>
            <span className="text-[#8B5CF6] font-bold shrink-0">{money(d.value)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}


function Affiliates({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/affiliates`, { headers: { Authorization: `Bearer ${token}` } });
      setData(data);
    } catch { /* ignore */ } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  if (loading && !data) return <div className="py-16 text-center text-[#7C859B] text-sm">Loading…</div>;
  const s = data?.summary || {};
  const rows = (data?.affiliates || []).filter((r) =>
    !q || (r.email || "").toLowerCase().includes(q.toLowerCase()) || (r.code || "").toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <div className="grid grid-cols-3 gap-3 mb-4">
        <Stat icon={Users} label="Codes" value={s.total_codes ?? "—"} tint="#8B5CF6" />
        <Stat icon={TrendingUp} label="Referrals" value={s.total_referrals ?? "—"} tint="#34D399" />
        <Stat icon={DollarSign} label="Promo paid" value={money(s.total_promo_paid || 0)} tint="#FBBF24" />
      </div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#586074]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search email or code"
            className="w-full bg-[#120F22] border border-[#2A2440] pl-8 pr-3 py-2 text-xs text-white outline-none focus:border-[#8B5CF6]" />
        </div>
        <button data-testid="aff-refresh" onClick={load} className="ml-auto text-[11px] font-bold px-3 py-2 border border-[#2A2440] hover:bg-[#19142A] flex items-center gap-1 text-[#AEB6C6]"><RefreshCw className={`h-3 w-3 ${loading ? "animate-spin" : ""}`} />Refresh</button>
      </div>
      <div className="border border-[#2A2440] overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#120F22] text-[#7C859B] uppercase text-[10px]">
            <tr>
              <th className="text-left px-3 py-2">User</th>
              <th className="text-left px-3 py-2">Code</th>
              <th className="text-right px-3 py-2">Referrals</th>
              <th className="text-right px-3 py-2">Promo earned</th>
              <th className="text-right px-3 py-2">Promo bal</th>
              <th className="text-left px-3 py-2">Referred by</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} data-testid="aff-row" className="border-t border-[#2A2440]">
                <td className="px-3 py-2"><div className="text-white font-semibold">{r.name}</div><div className="text-[#586074]">{r.email}</div></td>
                <td className="px-3 py-2"><span className="font-mono font-bold text-[#C4B5FD]">{r.code}</span></td>
                <td className="px-3 py-2 text-right text-white">{r.referrals}</td>
                <td className="px-3 py-2 text-right text-[#34D399] font-bold">{money(r.promo_earned)}</td>
                <td className="px-3 py-2 text-right text-[#AEB6C6]">{money(r.promo_credits)}</td>
                <td className="px-3 py-2 text-[#7C859B]">{r.referred_by || "—"}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={6} className="px-3 py-10 text-center text-[#586074]">No affiliates yet</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}


const TIER_COLOR = {
  common: "#8A93A6", uncommon: "#34D399", rare: "#38BDF8",
  epic: "#A78BFA", legendary: "#FBBF24", mythic: "#F43F5E",
};
const statusColor = (s) => ({ owned: "#34D399", sold: "#7C859B", ship_requested: "#A78BFA", cash_requested: "#FBBF24", shipped: "#38BDF8", cashed_out: "#38BDF8", reserved: "#FBBF24" }[s] || "#7C859B");

function UserInventoryModal({ token, user, onClose }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/users/${user.id}/inventory`, { headers: { Authorization: `Bearer ${token}` } });
        if (live) setData(data);
      } catch (e) { if (live) setErr(e?.response?.data?.detail || "Failed to load inventory"); }
    })();
    return () => { live = false; };
  }, [token, user.id]);

  const s = data?.summary || {};
  const items = data?.items || [];

  return (
    <div data-testid="admin-inventory-modal" className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4"
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl max-h-[85vh] flex flex-col bg-[#19142A] border border-[#2A2440] text-[#E7E9F0]">
        <div className="flex items-start justify-between gap-4 p-5 border-b border-[#2A2440]">
          <div className="min-w-0">
            <div className="text-base font-extrabold flex items-center gap-2"><Package className="h-4 w-4 text-[#8B5CF6]" />{user.name}</div>
            <div className="text-xs text-[#7C859B]">{user.email}</div>
          </div>
          <button data-testid="admin-inventory-close" onClick={onClose}
            className="p-1.5 border border-[#2A2440] hover:bg-[#120F22] text-[#AEB6C6]"><X className="h-4 w-4" /></button>
        </div>

        {err ? (
          <div className="p-6 text-sm text-[#F43F5E]">{err}</div>
        ) : !data ? (
          <div className="p-10 text-center text-[#7C859B] text-sm">Loading inventory…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 p-5 border-b border-[#2A2440]">
              <div><div className="text-[10px] uppercase text-[#7C859B]">Balance</div><div className="text-lg font-bold font-mono text-[#34D399]">{money(user.balance)}</div></div>
              <div><div className="text-[10px] uppercase text-[#7C859B]">Owned items</div><div className="text-lg font-bold font-mono">{s.owned_items}</div></div>
              <div><div className="text-[10px] uppercase text-[#7C859B]">Owned value</div><div className="text-lg font-bold font-mono">{money(s.owned_value)}</div></div>
              <div><div className="text-[10px] uppercase text-[#7C859B]">Total won</div><div className="text-lg font-bold font-mono">{money(s.total_value)}</div></div>
            </div>
            <div className="overflow-y-auto">
              {items.length === 0 ? (
                <div className="p-10 text-center text-[#7C859B] text-sm">No items — this user hasn't won anything yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-[#0F0C16] text-[#7C859B] sticky top-0">
                    <tr>
                      {["Item", "Case", "Value", "Status", "Won"].map((h) => (
                        <th key={h} className={`px-4 py-2.5 text-left font-semibold uppercase text-[10px] tracking-wide ${["Value"].includes(h) ? "text-right" : ""}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((it, i) => (
                      <tr key={it.id || i} data-testid="admin-inventory-item" className="border-t border-[#1E1832]">
                        <td className="px-4 py-2.5 font-semibold" style={{ color: TIER_COLOR[it.tier] || "#E7E9F0" }}>{it.name}</td>
                        <td className="px-4 py-2.5 text-[#AEB6C6] text-xs">{it.case_name || (it.battle_id ? "Battle" : "—")}</td>
                        <td className="px-4 py-2.5 text-right font-mono">{money(it.value)}</td>
                        <td className="px-4 py-2.5"><span className="text-[10px] font-bold uppercase" style={{ color: statusColor(it.status) }}>{(it.status || "owned").replace("_", " ")}</span></td>
                        <td className="px-4 py-2.5 text-[11px] text-[#7C859B] whitespace-nowrap">{it.won_at ? new Date(it.won_at).toLocaleDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
